/**
 * EngagementRowChip — FR-8/9 per-row "help is in motion" chip.
 *
 * Rendered IN PLACE OF the standard status pill in a penalty row's status cell
 * when that row's managed matter carries a professional engagement (built by
 * `buildEngagementByMatterId` from the cases query). Replaces the retired
 * localStorage consult chip (ScheduledChip / HandledChip) with a chip driven by
 * the SERVER engagement state — one help-state machine, not two.
 *
 * Compliance-Broadsheet vocabulary: mono uppercase micro-type, CSS-var tokens
 * only (teal for a live engagement, ink-mute for a completed one), so it flips
 * with the /portal dark toggle. No hex.
 */
import * as React from "react";

import type {
  CaseEngagement,
  EngagementStatus,
  ProfessionalType,
} from "@/queries/violationCasesQueries";

/** Exported so the Exposure board's case chips read the SAME two words a row
 *  chip reads; a second map is two vocabularies for one engagement. */
// CODECLEAR and WITHDRAWN are the Action plan hand-off's; the query unions lack them.
export const PRO_LABEL: Record<ProfessionalType | "CODECLEAR", string> = {
  CODECLEAR: "CodeClear",
  LAWYER: "Attorney",
  EXPEDITER: "Expediter",
  VENDOR: "Vendor",
  OWN_PROFESSIONAL: "Your pro",
};

// Owner-facing status words. Intentionally plain — the machine status is the
// source of truth; this is just the human read of "where the request is".
export const STATUS_LABEL: Record<EngagementStatus | "WITHDRAWN", string> = {
  REQUESTED: "requested",
  SOURCING: "sourcing",
  FEE_APPROVAL: "fee approval",
  ENGAGED: "engaged",
  COMPLETED: "done",
  WITHDRAWN: "withdrawn",
};

const ENDED = new Set<string>(["COMPLETED", "WITHDRAWN"]);
export const isLive = (status: EngagementStatus): boolean => !ENDED.has(status);

export const EngagementRowChip: React.FC<{
  engagement: CaseEngagement;
  testId?: string;
  /** Set when the work has no window left (case page, B2 Leo 10-31): drawn muted, the note
   *  in the tooltip. Absent renders HEAD. */
  idleNote?: string;
  /** `portal_judgment_owed` ON: a comma where HEAD's tooltip has an em dash (copy rule). */
  plainCopy?: boolean;
}> = ({ engagement, testId, idleNote, plainCopy = false }) => {
  const pro = PRO_LABEL[engagement.professionalType] ?? "Help";
  const status = STATUS_LABEL[engagement.status] ?? engagement.status;
  const live = isLive(engagement.status) && !idleNote;
  const label = idleNote
    ? `${pro} ${status}, ${idleNote}`
    : plainCopy
      ? `${pro}, ${status}`
      : `${pro} — ${status}`;
  return (
    <span
      data-testid={testId}
      data-professional-type={engagement.professionalType}
      data-status={engagement.status}
      data-live={isLive(engagement.status) ? "1" : "0"}
      data-idle={idleNote ? "1" : undefined}
      aria-label={`Help requested: ${label}`}
      title={label}
      className={
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.06em] " +
        (live
          ? "border-[hsl(var(--teal-border))] bg-[hsl(var(--teal-soft))] text-[hsl(var(--teal-ink))]"
          : "border-[hsl(var(--ink-line))] bg-transparent text-[hsl(var(--ink-mute))]")
      }
    >
      <span
        aria-hidden="true"
        className={
          "inline-block h-1.5 w-1.5 flex-none rounded-full " +
          (live ? "bg-[hsl(var(--teal))]" : "bg-[hsl(var(--ink-line-strong))]")
        }
      />
      <span className="font-semibold">{pro}</span>
      <span aria-hidden="true" className="text-[hsl(var(--ink-faint))]">
        ·
      </span>
      <span>{status}</span>
    </span>
  );
};

export default EngagementRowChip;
